import { Lightbulb } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

type Tip = {
  title: string
  body: string
  example?: { before: string; after: string }
  /** A mechanical fix for the current "spoken as" text, offered as a button when it would change something. */
  action?: { label: string; apply: (text: string) => string }
}

// Steering ElevenLabs by editing the text it reads. Sources:
// elevenlabs.io/docs/overview/capabilities/text-to-speech/best-practices
// (pauses, break tags, respelling, abbreviations, narrative tags being spoken)
// and elevenlabs.io/blog/v3-audiotags (audio tags). "Stretch the vowel" is a
// field-tested trick, not official guidance — it says so.
const TIPS: Tip[] = [
  {
    title: 'Listen first',
    body: 'Generate the line as written before changing anything. Often it’s fine — and if not, you’ll hear exactly what to fix.',
  },
  {
    title: 'Drawn-out words',
    body: 'Repeated letters confuse the voice (“CHARLESSSSS” → “Char-less”). Remove them, then either put [drawn out] before the word (v4) or try stretching the stressed vowel — not official guidance, but it often works.',
    example: { before: 'CHARLESSSSS!!', after: '[drawn out] Charles!!  or  Chaaaarles!!' },
    action: { label: 'Remove repeated letters', apply: (text) => text.replace(/([A-Za-z])\1{2,}/g, '$1') },
  },
  {
    title: 'Pauses',
    body: 'A comma is a breath, “…” trails off, “—” cuts off. For an exact beat on a standard take, add a break tag (up to 3 seconds, sparingly — too many can make a take unstable). Break tags don’t work with audio tags (v4); use [pause] there.',
    example: { before: 'Charles! Charles!', after: 'Charles! <break time="0.8s" /> Charles!' },
  },
  {
    title: 'Punctuation and capitals',
    body: '! lifts energy, ? lifts the end, and CAPITALS add emphasis. If an all-caps word comes out spelled out or odd, try it in normal case with an !.',
    example: { before: 'Oh, right. EVERYTHING.', after: 'Oh, right. Everything!' },
    action: {
      label: 'Normal-case the CAPS words',
      apply: (text) => text.replace(/\b[A-Z]{3,}\b/g, (w) => w[0] + w.slice(1).toLowerCase()),
    },
  },
  {
    title: 'Don’t describe the emotion in words',
    body: 'Words like “she shouted” are read aloud. Steer with punctuation and wording — the voice already hears the lines before and after for context. Notes in (parentheses) are removed, so they’re safe.',
  },
  {
    title: 'Spell names as they sound',
    body: 'Respell unusual names phonetically; hyphens, apostrophes and capitals can mark syllables and stress.',
    example: { before: 'Dennys', after: 'Dennis' },
  },
  {
    title: 'Spell out abbreviations',
    body: 'Write titles, symbols and numbers the way they’re said.',
    example: { before: 'Mrs Whatsit', after: 'Missus Whatsit' },
    action: {
      label: 'Spell out Mr / Mrs / Dr',
      apply: (text) =>
        text
          .replace(/\bMrs\.?(?=\s)/g, 'Missus')
          .replace(/\bMr\.?(?=\s)/g, 'Mister')
          .replace(/\bDr\.?(?=\s)/g, 'Doctor'),
    },
  },
  {
    title: 'Still not right? Variations',
    body: 'Every take is read a little differently. Generate 3 and keep the best before rewriting the line again.',
  },
]

// Audio tags that suit theatre lines. They switch the take to ElevenLabs'
// expressive model (v4); untagged lines stay on the standard one.
const TAG_GROUPS: { label: string; tags: string[] }[] = [
  { label: 'Pace', tags: ['[slowly]', '[drawn out]', '[rushed]', '[pause]', '[interrupting]'] },
  { label: 'Volume', tags: ['[shouts]', '[whispers]', '[softly]'] },
  {
    label: 'Feeling',
    tags: ['[worried]', '[panicking]', '[excited]', '[sad]', '[angry]', '[cautiously]', '[sarcastic]'],
  },
  { label: 'Reactions', tags: ['[sighs]', '[laughs]', '[chuckles]', '[clears throat]'] },
]

export function SpeechTips({
  current,
  onTry,
  onInsertTag,
}: {
  current: string
  onTry: (text: string) => void
  onInsertTag: (tag: string) => void
}) {
  return (
    <Card className="self-start">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lightbulb className="size-4" /> Getting the reading right
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-2 rounded-md border bg-muted/40 p-3 text-sm">
          <p className="font-medium">Audio tags</p>
          <p className="text-muted-foreground">
            Direct the delivery in [brackets] — before a word or sentence, and you can combine them. Tagged takes use
            the expressive v4 model automatically. Click to insert at the cursor:
          </p>
          {TAG_GROUPS.map((group) => (
            <div key={group.label} className="flex flex-wrap items-center gap-1">
              <span className="w-16 text-xs text-muted-foreground">{group.label}</span>
              {group.tags.map((tag) => (
                <Button
                  key={tag}
                  variant="outline"
                  size="sm"
                  className="h-6 px-1.5 font-mono text-[11px]"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onInsertTag(tag)}
                >
                  {tag}
                </Button>
              ))}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            A tag that doesn’t suit the voice (a soft voice told to [shouts]) may be spoken aloud — try a gentler tag or
            another variation.
          </p>
        </div>
        {TIPS.map((tip) => {
          const applied = tip.action?.apply(current)
          const canApply = applied !== undefined && applied !== current
          return (
            <div key={tip.title} className="grid gap-1 text-sm">
              <p className="font-medium">{tip.title}</p>
              <p className="text-muted-foreground">{tip.body}</p>
              {tip.example && (
                <p className="text-xs">
                  <span className="text-muted-foreground line-through">{tip.example.before}</span> →{' '}
                  <span className="font-medium">{tip.example.after}</span>
                </p>
              )}
              {canApply && (
                <Button variant="secondary" size="sm" className="h-7 justify-self-start" onClick={() => onTry(applied)}>
                  {tip.action?.label}
                </Button>
              )}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
